/**
 * @module scroll-utils
 * Continuous scroll-position tracking and CSS custom property binding driven by Lenis.
 */

/* ─── Reduced motion ─────────────────────────────────────────────────────── */

const _reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ─── Debug Panel ────────────────────────────────────────────────────────── */

const _isDebug = () => document.documentElement.hasAttribute('data-scroll-debug');
let _debugPanel = null;

function _updateDebugPanel(varElements) {
  if (!_isDebug()) {
    if (_debugPanel) {
      _debugPanel.remove();
      _debugPanel = null;
    }
    return;
  }

  if (!_debugPanel) {
    _debugPanel = document.createElement('div');
    _debugPanel.id = '_scroll-bind-debug';
    _debugPanel.style.cssText = [
      'position:fixed;bottom:0;left:0;max-height:40vh;overflow-y:auto',
      'background:rgba(0,0,0,.88);color:#0f0;font:11px/1.6 monospace',
      'padding:8px 12px;z-index:99999;pointer-events:none;min-width:300px',
    ].join(';');
    document.body.appendChild(_debugPanel);
  }

  const rows = varElements.flatMap(({ el, configs, currentVals }) =>
    configs.map(cfg => {
      const id  = el.id ? `#${el.id}` : el.className.split(' ').find(Boolean) ?? 'el';
      const val = (currentVals[cfg.var] ?? 0).toFixed(3);
      return `<div><span style="color:#888">${id}</span> <span style="color:#aaa">${cfg.var}</span> <b>${val}</b></div>`;
    })
  );

  _debugPanel.innerHTML = `<div style="color:#ff0;margin-bottom:4px">[scroll-bind]</div>${rows.join('')}`;
}

/* ─── Anchor Parsing & Progress Calculations ────────────────────────────── */

/**
 * Parse an anchor string: "<element-edge> <viewport-position>"
 *   element-edge      : top | center | bottom          (default: top)
 *   viewport-position : top | center | bottom | 0–100  (default: 0)
 *
 * @param {string} str  e.g. "top bottom", "center 50", "bottom 0"
 * @returns {{ elemFrac: number, vpFrac: number }}
 */
export function parseAnchor(str) {
  const parts    = (str || '').trim().split(/\s+/);
  const elemPart = parts[0] || 'top';
  const vpPart   = parts[1] || '0';

  const elemFrac =
    elemPart === 'bottom' ? 1 :
    elemPart === 'center' ? 0.5 : 0;

  const vpFrac =
    vpPart === 'bottom' ? 1 :
    vpPart === 'center' ? 0.5 :
    vpPart === 'top'    ? 0 :
    parseFloat(vpPart) / 100;

  return { elemFrac, vpFrac };
}

/**
 * Returns a 0–1 progress value for a given rect and anchor pair.
 *
 * @param {DOMRect} rect
 * @param {number}  windowHeight
 * @param {string}  startAnchor
 * @param {string}  endAnchor
 * @returns {number} 0–1
 */
export function computeProgress(rect, windowHeight, startAnchor, endAnchor) {
  const { elemFrac: es, vpFrac: vs } = parseAnchor(startAnchor);
  const { elemFrac: ee, vpFrac: ve } = parseAnchor(endAnchor);

  const numerator   = windowHeight * vs - rect.top - es * rect.height;
  const denominator = (ee - es) * rect.height + windowHeight * (vs - ve);

  if (denominator === 0) return 0;
  return Math.max(0, Math.min(1, numerator / denominator));
}

/* ─── Consolidated Continuous Scroll Engine ─────────────────────────────── */

let _activeLenis           = null;
let _progressSelector      = null;
let _progressElements      = [];
let _percentActive         = false;
let _percentElements       = [];
let _varActive             = false;
let _varElements           = []; // { el, originEl, configs, currentVals }[]

let _isScrollListening     = false;
let _isResizeListening     = false;

function _parseScrollBindConfig(el) {
  try {
    const cfg = JSON.parse(el.dataset.scrollBind);
    return Array.isArray(cfg) ? cfg : [];
  } catch {
    console.warn('[scroll-utils] Invalid JSON in data-scroll-bind on', el);
    return [];
  }
}

function _resolveOrigin(el) {
  const sel = el.dataset.scrollOrigin;
  if (!sel) return el;
  return el.closest(sel) || document.querySelector(sel) || el;
}

function _cacheVarElements() {
  _varElements = Array.from(document.querySelectorAll('[data-scroll-bind]'))
    .map(el => {
      const configs     = _parseScrollBindConfig(el);
      const currentVals = {};
      configs.forEach(cfg => { currentVals[cfg.var] = null; });
      return { el, originEl: _resolveOrigin(el), configs, currentVals };
    })
    .filter(({ configs }) => configs.length > 0);
}

function _runTick() {
  // 1. Page progress bars
  if (_activeLenis && _progressElements.length > 0) {
    const limit = _activeLenis.limit || 0;
    const pct = limit > 0
      ? Math.max(0, Math.min(100, (_activeLenis.scroll / limit) * 100))
      : 0;
    _progressElements.forEach(el => { el.style.width = `${pct}%`; });
  }

  const wh = window.innerHeight;

  // 2. Element scroll percent (--scroll-percent)
  if (_percentElements.length > 0) {
    _percentElements.forEach(el => {
      const progress = computeProgress(
        el.getBoundingClientRect(), wh,
        el.dataset.scrollStart ?? 'top bottom',
        el.dataset.scrollEnd   ?? 'top 0'
      );
      el.style.setProperty('--scroll-percent', (progress * 100).toFixed(2));
    });
  }

  // 3. Multi-variable bindings (data-scroll-bind)
  if (_varElements.length > 0) {
    _varElements.forEach(({ el, originEl, configs, currentVals }) => {
      const rect = originEl.getBoundingClientRect();
      configs.forEach(cfg => {
        const target = computeProgress(rect, wh, cfg.start ?? 'top bottom', cfg.end ?? 'top 0');
        const prev   = currentVals[cfg.var];
        const value  = (cfg.ease && prev !== null) ? prev + (target - prev) * cfg.ease : target;
        currentVals[cfg.var] = value;
        el.style.setProperty(cfg.var, value.toFixed(4));
      });
    });
    _updateDebugPanel(_varElements);
  }
}

function _onResize() {
  if (_progressSelector) {
    _progressElements = Array.from(document.querySelectorAll(_progressSelector));
  }
  if (_percentActive) {
    _percentElements = Array.from(document.querySelectorAll('[data-scroll-percent]'));
  }
  if (_varActive) {
    _cacheVarElements();
  }
  _runTick();
}

function _syncListeners(lenisInstance) {
  if (lenisInstance) _activeLenis = lenisInstance;

  const hasActiveFeatures = Boolean(_progressSelector || _percentActive || _varActive);

  if (hasActiveFeatures) {
    if (!_isResizeListening) {
      window.addEventListener('resize', _onResize);
      _isResizeListening = true;
    }
    if (_activeLenis && !_reducedMotion.matches && !_isScrollListening) {
      _activeLenis.on('scroll', _runTick);
      _isScrollListening = true;
    }
  } else {
    if (_isScrollListening && _activeLenis) {
      _activeLenis.off('scroll', _runTick);
      _isScrollListening = false;
    }
    if (_isResizeListening) {
      window.removeEventListener('resize', _onResize);
      _isResizeListening = false;
    }
  }
}

/* ─── Public API ─────────────────────────────────────────────────────────── */

/**
 * Tracks page-level scroll position as a percentage-width on matching elements.
 * @param {string} selector      CSS selector for progress bar elements.
 * @param {object} lenisInstance
 */
export function initializeScrollProgressIndicators(selector, lenisInstance) {
  if (!lenisInstance) {
    console.error('[scroll-utils] Lenis instance required for scroll progress indicators.');
    return;
  }
  _progressSelector = selector;
  _progressElements = Array.from(document.querySelectorAll(selector));
  _syncListeners(lenisInstance);
  _runTick();
}

export function destroyScrollProgressIndicators() {
  _progressSelector = null;
  _progressElements = [];
  _syncListeners();
}

/**
 * Initialise scroll-percent tracking for all [data-scroll-percent] elements.
 * @param {object} lenisInstance
 */
export function initScrollPercentElements(lenisInstance) {
  if (!lenisInstance) {
    console.error('[scroll-utils] Lenis instance required for initScrollPercentElements.');
    return;
  }
  _percentActive   = true;
  _percentElements = Array.from(document.querySelectorAll('[data-scroll-percent]'));
  _syncListeners(lenisInstance);
  _runTick();
}

export function refreshScrollPercentElements() {
  if (!_percentActive) return;
  _percentElements = Array.from(document.querySelectorAll('[data-scroll-percent]'));
  _runTick();
}

export function destroyScrollPercentElements() {
  _percentActive   = false;
  _percentElements = [];
  _syncListeners();
}

/**
 * Initialise multi-var scroll tracking for all [data-scroll-bind] elements.
 * @param {object} lenisInstance
 */
export function initScrollVarElements(lenisInstance) {
  if (!lenisInstance) {
    console.error('[scroll-utils] Lenis instance required for initScrollVarElements.');
    return;
  }
  _varActive = true;
  _cacheVarElements();
  _syncListeners(lenisInstance);
  _runTick();
}

export function refreshScrollVarElements() {
  if (!_varActive) return;
  _cacheVarElements();
  _runTick();
}

export function destroyScrollVarElements() {
  _varActive   = false;
  _varElements = [];
  if (_debugPanel) {
    _debugPanel.remove();
    _debugPanel = null;
  }
  _syncListeners();
}

/**
 * Refresh all scroll-utils tracking systems after dynamic DOM insertion.
 */
export function refreshScrollElements() {
  refreshScrollPercentElements();
  refreshScrollVarElements();
}